import { createStart, createMiddleware, createCsrfMiddleware } from "@tanstack/react-start";

const csrfMiddleware = createCsrfMiddleware({
  filter: (ctx) => ctx.handlerType === "serverFn",
});

import { renderErrorPage } from "./lib/error-page";
import { prefersMarkdown } from "./lib/markdown-negotiation";
import { attachSupabaseAuth } from "@/integrations/supabase/auth-attacher";

const errorMiddleware = createMiddleware().server(async ({ next }) => {
  try {
    return await next();
  } catch (error) {
    if (error != null && typeof error === "object" && "statusCode" in error) {
      throw error;
    }
    console.error(error);
    return new Response(renderErrorPage(), {
      status: 500,
      headers: { "content-type": "text/html; charset=utf-8" },
    });
  }
});

/**
 * Markdown content negotiation.
 *
 * Serves a clean Markdown representation of the page when the client
 * sends `Accept: text/markdown` (preferred over text/html) or requests
 * the URL with a `.md` suffix (e.g. /blog/foo.md). Non-content routes
 * (tools, APIs, sitemaps, feeds) pass through to normal SSR.
 */
const markdownNegotiationMiddleware = createMiddleware().server(async ({ next, request }) => {
  if (request.method !== "GET") return next();
  const url = new URL(request.url);
  const path = url.pathname;

  // Never intercept infrastructure / API / asset paths.
  if (
    path.startsWith("/api/") ||
    path.startsWith("/_") ||
    path.startsWith("/assets/") ||
    path.includes(".") && !path.endsWith(".md")
  ) {
    return next();
  }

  const wantsMd = path.endsWith(".md") || prefersMarkdown(request.headers.get("accept"));
  if (!wantsMd) return next();

  const { renderRouteMarkdown } = await import("./lib/markdown-render");
  const md = renderRouteMarkdown(path);
  if (!md) return next();

  const canonicalPath = path.endsWith(".md") ? path.slice(0, -3) || "/" : path;
  return new Response(md, {
    headers: {
      "Content-Type": "text/markdown; charset=utf-8",
      "Cache-Control": "public, max-age=3600",
      Vary: "Accept",
      Link: `<${new URL(canonicalPath, url.origin).toString()}>; rel="canonical"`,
    },
  });
});

/**
 * Edge caching for SSR HTML.
 *
 * The site is fully static content per URL, so the CDN can serve a cached
 * copy while revalidating in the background — that removes the SSR render
 * from TTFB for repeat visitors and crawlers. Browsers still revalidate
 * (`max-age=0`) so a publish is visible immediately.
 * Personalized routes are explicitly excluded.
 */
const PRIVATE_PREFIXES = ["/admin", "/auth", "/api/"];

const htmlCacheMiddleware = createMiddleware().server(async ({ next, request }) => {
  const result = await next();
  const response = (result as { response?: Response }).response;
  if (!response || request.method !== "GET" || response.status !== 200) return result;

  const path = new URL(request.url).pathname;
  const contentType = response.headers.get("content-type") ?? "";
  if (!contentType.includes("text/html")) return result;
  if (response.headers.has("cache-control")) return result;

  response.headers.set(
    "Cache-Control",
    PRIVATE_PREFIXES.some((p) => path.startsWith(p))
      ? "private, no-store"
      : "public, max-age=0, s-maxage=600, stale-while-revalidate=86400",
  );
  return result;
});

/**
 * Permanent (301) redirects: www → apex, and old WordPress URLs that Google
 * still lists, so their link value passes to the current pages.
 */
const LEGACY_REDIRECTS: Record<string, string> = {
  "/blog/category/strategy": "/blog/category/seo-strategy",
  "/blog/category/research": "/blog/category/original-research",
  "/author/freelancertamal": "/about",
  "/author/tamal32": "/about",
  "/accessibility-statement": "/trust",
  "/cart": "/pricing",
  "/best-seo-expert-in-rangpur": "/seo-services/rangpur",
  "/how-to-conduct-a-professional-seo-audit": "/free-seo-audit",
};

const redirectMiddleware = createMiddleware().server(async ({ next, request }) => {
  const url = new URL(request.url);
  if (url.hostname.startsWith("www.")) {
    url.hostname = url.hostname.slice(4);
    return new Response(null, { status: 301, headers: { Location: url.toString() } });
  }
  const path = url.pathname.replace(/\/+$/, "").toLowerCase();
  const target = LEGACY_REDIRECTS[path] ?? (path.startsWith("/author/") ? "/about" : undefined);
  if (target) {
    return new Response(null, { status: 301, headers: { Location: new URL(target, url.origin).toString() } });
  }
  return next();
});

export const startInstance = createStart(() => ({
  functionMiddleware: [attachSupabaseAuth],
  requestMiddleware: [redirectMiddleware, csrfMiddleware, errorMiddleware, markdownNegotiationMiddleware, htmlCacheMiddleware],
}));

